// Lazily loads locale bundles so only English ships in the eager bundle. This
// module must stay free of i18next imports: the app entry calls
// prefetchLocale before the main app chunk (and i18next) has loaded.

import candidateLocales from './candidateLocales.json';

type LocaleJson = Record<string, unknown>;

export const englishCommonCodes = [
  'en',
  ...Object.entries(candidateLocales.aliases)
    .filter(([, parent]) => parent === 'en')
    .map(([code]) => code),
];

export const supportedLanguages: string[] = [
  ...candidateLocales.full,
  ...Object.keys(candidateLocales.overrides),
  ...Object.keys(candidateLocales.aliases),
];

// Explicit imports keep each bundle independently lazy and statically discoverable
// by Vite. The always-on locale check holds these keys to candidateLocales.json.
const localeImports: Record<string, () => Promise<{ default: LocaleJson }>> = {
  es: () => import('./locales/es/common.json'),
  'es-ES': () => import('./locales/es-ES/common.json'),
  de: () => import('./locales/de/common.json'),
  zh: () => import('./locales/zh/common.json'),
  ja: () => import('./locales/ja/common.json'),
  nl: () => import('./locales/nl/common.json'),
  'nl-BE': () => import('./locales/nl-BE/common.json'),
  fr: () => import('./locales/fr/common.json'),
  'fr-CA': () => import('./locales/fr-CA/common.json'),
  it: () => import('./locales/it/common.json'),
  hi: () => import('./locales/hi/common.json'),
  'pt-BR': () => import('./locales/pt-BR/common.json'),
  'pt-PT': () => import('./locales/pt-PT/common.json'),
};

const aliases = new Map(Object.entries(candidateLocales.aliases));

export const localeDependencies: Readonly<Record<string, string>> = {
  ...candidateLocales.overrides,
  ...candidateLocales.aliases,
};

/** Candidate UI fallback chains, shared by loading and i18next resolution. */
export const candidateLocaleFallbacks: Record<string, string[]> = {
  ...Object.fromEntries(
    supportedLanguages.map((code) => {
      const chain: string[] = [];
      let parent = localeDependencies[code];
      while (parent && parent !== 'en') {
        chain.push(parent);
        parent = localeDependencies[parent];
      }
      return [code, [...chain, 'en']];
    })
  ),
  default: ['en'],
};

/** Resolves a supported candidate locale without defaulting an invalid hint. */
export const resolveCandidateLocale = (lang: string): string | null => {
  const normalized = lang.trim().toLowerCase();
  const exact = supportedLanguages.find(
    (code) => code.toLowerCase() === normalized
  );
  if (exact) return exact;
  const [base] = normalized.split('-');
  if (base === undefined) return null;
  return supportedLanguages.includes(base) ? base : null;
};

export const normalizeLangParam = (lang: string): string =>
  resolveCandidateLocale(lang) ?? 'en';

const fetchCache = new Map<string, Promise<LocaleJson>>();

const fetchLocaleJson = (code: string): Promise<LocaleJson> => {
  const bundleCode = aliases.get(code) ?? code;
  let pending = fetchCache.get(bundleCode);
  if (!pending) {
    const importLocale = localeImports[bundleCode];
    if (!importLocale) {
      throw new Error(`No candidate locale bundle for "${bundleCode}"`);
    }
    pending = importLocale().then((mod) => mod.default);
    fetchCache.set(bundleCode, pending);
    // Drop failed fetches from the cache so a later attempt can retry.
    pending.catch(() => fetchCache.delete(bundleCode));
  }
  return pending;
};

export interface LocaleBundle {
  code: string;
  bundle: LocaleJson;
}

export const needsLocaleFetch = (lang: string): boolean =>
  Object.hasOwn(localeImports, aliases.get(lang) ?? lang);

/**
 * Fetches the bundle for `lang` plus the locale it falls back to (or merges
 * with), returned ready to register in fallback-first order.
 */
export const getLocaleBundles = async (
  lang: string
): Promise<LocaleBundle[]> => {
  if (!needsLocaleFetch(lang)) {
    return [];
  }

  const dependency = localeDependencies[lang];
  if (!dependency) {
    return [{ code: lang, bundle: await fetchLocaleJson(lang) }];
  }

  const [dependencyBundle, targetBundle] = await Promise.all([
    fetchLocaleJson(dependency),
    fetchLocaleJson(lang),
  ]);
  const bundle =
    lang === 'nl-BE' ? { ...dependencyBundle, ...targetBundle } : targetBundle;
  return [
    { code: dependency, bundle: dependencyBundle },
    { code: lang, bundle },
  ];
};

// One-way handshake with i18n.ts, kept as a bare promise because this module
// cannot import i18next. Resolved exactly once, by markInitialLocaleRegistered.
let resolveInitialLocaleRegistered: () => void = () => {};
const initialLocaleRegistered = new Promise<void>((resolve) => {
  resolveInitialLocaleRegistered = resolve;
});

/**
 * Starts downloading the locale chunks for a ?lang= value without touching
 * i18next, so the app entry can warm them in parallel with the app chunk.
 * Never rejects: this is best-effort warmup, and registration is what surfaces
 * a real failure.
 */
export const prefetchLocale = async (
  langParam: string | null
): Promise<void> => {
  if (!langParam) {
    return;
  }
  const lang = normalizeLangParam(langParam);
  if (!needsLocaleFetch(lang)) {
    return;
  }
  try {
    await getLocaleBundles(lang);
  } catch {
    // swallow-ok: Speculative locale warmup does not register resources; failed fetches are evicted and loadAndChangeLanguage retries with registered English fallback.
    // Best-effort warmup; registration retries and surfaces failures.
  }
};

/**
 * Resolves once the bundle for the entry's ?lang= is registered with i18next,
 * and immediately when that value needs no chunk at all. The app entry gates
 * its first render on this, so a candidate never sees an English paint that
 * swaps to their language a beat later.
 */
export const whenInitialLocaleRegistered = (
  langParam: string | null
): Promise<void> => {
  if (!langParam || !needsLocaleFetch(normalizeLangParam(langParam))) {
    return Promise.resolve();
  }
  return initialLocaleRegistered;
};

/**
 * Called by i18n.ts once the initial bundle is registered or has failed to
 * load, so a failed locale releases the render gate rather than holding it.
 */
export const markInitialLocaleRegistered = (): void => {
  resolveInitialLocaleRegistered();
};
